import React, { useEffect, useMemo, useState } from 'react'
import { AiFillEdit, AiOutlineInfoCircle } from 'react-icons/ai'
import { BiSolidTrash } from 'react-icons/bi'

import { BodyPart } from '../../api/services/body_parts/types.ts'
import { bodyMeasurementsApi } from '../../api/services/dd_panel_body_measurements'
import { bodyMeasurementKeys } from '../../api/services/dd_panel_body_measurements/queryKeys.ts'
import {
  AddMeasurementData,
  BodyMeasurement,
  BodyMeasurementResponse
} from '../../api/services/dd_panel_body_measurements/types.ts'
import { useAuth } from '../../context/auth'
import { useBodyMeasurementMutations } from '../../hooks/useBodyMeasurementMutations.ts'
import {
  setBodyMeasurementsTableActions,
  setBodyMeasurementsTableColumns
} from '../../utils/bodyMeasurementsTableSettings.tsx'
import {
  prepareBodyMeasurementsWithAdditional,
  prepareFields
} from '../../utils/bodyParts/helpers.ts'
import {
  ActionPopupMenu,
  ActionPopupMenuItem
} from '../common/ActionPopupMenuItem'
import Alert from '../common/Alert/Alert.tsx'
import Button from '../common/Button/Button.tsx'
import Loader from '../common/Loader/Loader.tsx'
import { Pagination } from '../common/Pagination'
import { usePaginatedQuery } from '../common/Pagination/usePaginatedQuery.ts'
import Popup from '../common/Popup/Popup.tsx'
import DataEntryForm from '../common/Table/DataEntryForm.tsx'
import Table from '../common/Table/Table.tsx'

const fetchBodyMeasurements = async ({
  page,
  filters
}: {
  page: number
  filters?: string
}): Promise<BodyMeasurementResponse | null> => {
  const filterParams = `?page=${page}${filters ? `${filters}` : ''}`
  const res =
    await bodyMeasurementsApi.getList<BodyMeasurementResponse>(filterParams)

  return res.data
}

const BodyMeasurementsTable = ({
  patient_profile,
  setBodyMeasurements,
  formAvailable = false,
  showForm = false,
  setShowForm,
  bodyParts,
  formOnStart
}: {
  patient_profile: number
  setBodyMeasurements?: React.Dispatch<
    React.SetStateAction<BodyMeasurement[] | null>
  >
  formAvailable?: boolean
  showForm?: boolean
  setShowForm?: React.Dispatch<React.SetStateAction<boolean>>
  bodyParts?: BodyPart[] | null
  formOnStart?: boolean
}) => {
  const { state } = useAuth()

  const [deleteID, setDeleteID] = useState<number | null>(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [selectedRow, setSelectedRow] = useState<BodyMeasurement | null>(null)
  const [editValues, setEditValues] = useState<Partial<BodyMeasurement> | null>(
    null
  )
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{
    top: number
    left: number
  } | null>(null)

  const hideMenuForAll = showForm

  const onClose = () => {
    setShowForm?.(false)
    setIsMenuOpen(false)
    setEditValues(null)
    setDeleteID(null)
    setShowDeleteConfirmation(false)
  }

  const {
    addMeasurementMutation,
    removeMeasurementMutation,
    editMeasurementMutation
  } = useBodyMeasurementMutations({
    onSuccess: onClose
  })

  const filterParams = patient_profile
    ? `&ordering=-measured_at&patient_profile=${patient_profile}`
    : undefined

  const { data, isLoading, isError, currentPage, goToPage, totalPages } =
    usePaginatedQuery<BodyMeasurementResponse>({
      queryKey: bodyMeasurementKeys.list(
        state.isAuthenticated,
        state.email,
        filterParams
      ),
      fetcher: ({ page, filterParams }) =>
        fetchBodyMeasurements({ page, filters: filterParams }),
      pageSize: 10,
      filterParams
    })

  const menuItems: ActionPopupMenuItem<BodyMeasurement>[] = [
    {
      label: 'Edytuj',
      icon: <AiFillEdit className="text-lg" />,
      onClick: (row) => {
        const add = prepareForEdit(row)
        const newRow = { ...row, ...add }
        setEditValues(newRow)
        if (setShowForm) setShowForm(true)
      },
      disabled: (row) => !row.created_by_dietitian
    },
    {
      label: 'Usuń',
      icon: <BiSolidTrash className="text-lg" />,
      className: 'text-red-600',
      onClick: (row) => {
        setDeleteID(row.id)
        setShowDeleteConfirmation(true)
      }
    }
  ]

  const columns = useMemo(
    () => setBodyMeasurementsTableColumns(formAvailable),
    []
  )

  const actions = setBodyMeasurementsTableActions((row, event) => {
    const rect = event.currentTarget.getBoundingClientRect()

    setSelectedRow(row)
    setMenuPosition({
      top: rect.bottom + 6,
      left: rect.right - 180
    })
    setIsMenuOpen(true)
  }, hideMenuForAll)

  const formFields = useMemo(() => {
    return prepareFields(columns, bodyParts as BodyPart[])
  }, [columns, bodyParts])

  if (isError) {
    return <div>Wystąpił błąd podczas zapytania na serwer</div>
  }

  useEffect(() => {
    if (!isLoading && data) {
      setBodyMeasurements && setBodyMeasurements(data.results)
    }
  }, [isLoading, data, setBodyMeasurements])

  useEffect(() => {
    if (formOnStart) {
      setShowForm && setShowForm(true)
    }
  }, [formOnStart])

  const bodyMeasurementsWithAdditional = useMemo(() => {
    return prepareBodyMeasurementsWithAdditional(data)
  }, [data])

  const prepareForEdit = (row: BodyMeasurement) => {
    let newPart = {}
    row.body_part_measurements.forEach((measurement) => {
      const key = `body_part_${measurement.body_part}`
      newPart = { ...newPart, [key]: measurement.measured_value }
    })
    return newPart
  }

  const handleAddMeasurement = async (values: Record<string, unknown>) => {
    const newMeasurement: BodyMeasurement = {
      ...values
    } as BodyMeasurement

    newMeasurement.body_part_measurements = []

    Object.entries(values).forEach(([key, value]) => {
      if (key.includes('body_part') && key !== 'body_part_measurements') {
        const bodyPartId = parseInt(key.split('_')[2], 10)

        const newMeasuredValue = String(value) === '' ? '0' : String(value)

        newMeasurement.body_part_measurements.push({
          body_part: bodyPartId,
          measured_value: newMeasuredValue
        })
      }
    })

    const measurementData: AddMeasurementData = {
      ...newMeasurement,
      patient_profile
    }

    editValues && editValues?.id
      ? editMeasurementMutation.mutate({
          measurementId: editValues.id,
          measurementData
        })
      : addMeasurementMutation.mutate(measurementData)
  }

  const resetOnCancel = () => {
    setShowDeleteConfirmation(false)
    setDeleteID(null)
  }

  return (
    <React.Fragment>
      {formFields.length > 0 && formAvailable && showForm && (
        <DataEntryForm
          fields={formFields}
          onSubmit={handleAddMeasurement}
          submitLabel="Zapisz pomiar"
          onClose={onClose}
          mode={editValues ? 'edit' : 'add'}
          editValues={editValues}
          showAllLevelsLabel={
            editValues
              ? 'Edytuj obwody ciała do pomiaru'
              : 'Dodaj obwody ciała do pomiaru'
          }
          hideAllLevelsLabel={'Ukryj obwody ciała'}
        />
      )}

      {isLoading && <Loader label={'Pobieranie danych...'} />}

      {bodyMeasurementsWithAdditional.length > 0 && !isLoading && (
        <div className="relative">
          {formAvailable && (
            <Table
              data={bodyMeasurementsWithAdditional}
              columns={columns}
              actions={actions}
            />
          )}
          {!formAvailable && (
            <Table data={bodyMeasurementsWithAdditional} columns={columns} />
          )}
          <ActionPopupMenu
            row={selectedRow}
            isOpen={isMenuOpen}
            items={menuItems}
            position={menuPosition}
            onClose={() => setIsMenuOpen(false)}
          />
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={goToPage}
      />

      {!isLoading && data && data.results.length === 0 && (
        <div>
          <Alert
            type="info"
            message="Brak danych"
            icon={<AiOutlineInfoCircle className="text-lg" />}
          />
        </div>
      )}

      <Popup
        size={'auto'}
        isVisible={showDeleteConfirmation}
        onClose={resetOnCancel}
      >
        <p className="text-xl font-bold p-6 uppercase">Uwaga</p>
        <p className="text-xl px-6 mb-2">
          Wszystkie pomiary i obwody ciała utworzone w tym samym dniu zostaną
          usunięte.
        </p>
        <div className="w-full flex justify-end my-8 gap-x-4 px-8">
          <Button
            variant="filled"
            size="medium"
            color="primary"
            type="button"
            onClick={resetOnCancel}
            className="px-4 bg-yellow-500 text-white rounded-md shadow-md hover:bg-gray-400 transition-colors"
          >
            Anuluj
          </Button>
          <Button
            variant="filled"
            size="medium"
            color="primary"
            type="button"
            onClick={() =>
              deleteID && removeMeasurementMutation.mutate(deleteID)
            }
            className="px-4 bg-green-700 text-white rounded-md shadow-md hover:bg-green-600 transition-colors"
          >
            Usuń
          </Button>
        </div>
      </Popup>
    </React.Fragment>
  )
}

export default React.memo(BodyMeasurementsTable)
